/**
 * Team context for interaction events (owner_type / team_id / actor_role).
 *
 * Under RFC-1372 the paying wallet is derived from the PROJECT's owning team, so
 * project-scoped events (a Generate click, a header click) report that team,
 * not the active team in the header. The workspace registers it once per
 * project via `useProjectTeamAnalytics` (useProjectTeamAnalytics.ts); emitters
 * read it with `projectTeamContext`. Unknown context is omitted, never guessed.
 *
 * Type-only imports on purpose: api/client imports nodeAnalytics, which reads
 * this store, so a runtime import of the API layer here would be a cycle.
 */
import type { Team } from '../api/teams'
import type { OwnerType, TeamActorRole } from './generated/analyticsEvents'

export interface TeamAnalyticsContext {
  owner_type?: OwnerType
  team_id?: string
  actor_role?: TeamActorRole
}

const byProject = new Map<string, TeamAnalyticsContext>()

/** owner_type / team_id / actor_role for one team, or {} when it is unknown. */
export function teamContextFor(team: Team | null | undefined): TeamAnalyticsContext {
  if (!team) return {}
  return { owner_type: team.kind === 'personal' ? 'personal' : 'team', team_id: team.id, actor_role: team.myRole }
}

/** The registered context of a project's owning team, or {} when unknown. */
export function projectTeamContext(projectId: string | null | undefined): TeamAnalyticsContext {
  if (!projectId) return {}
  return byProject.get(projectId) ?? {}
}

export function setProjectTeamContext(projectId: string, team: Team | null | undefined): void {
  if (team) byProject.set(projectId, teamContextFor(team))
  else byProject.delete(projectId)
}

/** @internal test seam */
export function _resetTeamContextForTest(): void {
  byProject.clear()
}
